/**
 * Service de gestion des achats in-app via StoreKit natif iOS
 * Utilise cordova-plugin-purchase pour les achats natifs
 */
import { Capacitor } from "@capacitor/core";

// Product IDs - doit correspondre à App Store Connect
export const PRODUCT_IDS = {
  MONTHLY: "skybday_premium_monthly",
  SMS_10: "skybday_sms_10",
  SMS_50: "skybday_sms_50",
  SMS_100: "skybday_sms_100",
  // Message Packs
  PACK_PROCHES: "skybday_pack_proches",
  PACK_BUSINESS: "skybday_pack_business",
  PACK_HUMOUR: "skybday_pack_humour",
  PACK_ROMANTIQUE: "skybday_pack_romantique",
  PACK_ENFANTS: "skybday_pack_enfants",
  PACK_MEGA: "skybday_pack_mega",
};

// Entitlement ID
export const ENTITLEMENT_ID = "premium";

class PurchaseService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.isInitialized = false;
    this.store = null;
    this.products = {};
    this.isPremiumActive = false;
  }

  async initialize(userId) {
    try {
      if (this.isNative && window.CdvPurchase) {
        // Initialize native StoreKit via cordova-plugin-purchase
        this.store = window.CdvPurchase.store;
        
        // Register products
        this.store.register([
          {
            id: PRODUCT_IDS.MONTHLY,
            type: window.CdvPurchase.ProductType.PAID_SUBSCRIPTION,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.SMS_10,
            type: window.CdvPurchase.ProductType.CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.SMS_50,
            type: window.CdvPurchase.ProductType.CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.SMS_100,
            type: window.CdvPurchase.ProductType.CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          // Message Packs (non-consumable)
          {
            id: PRODUCT_IDS.PACK_PROCHES,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.PACK_BUSINESS,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.PACK_HUMOUR,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.PACK_ROMANTIQUE,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.PACK_ENFANTS,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
          {
            id: PRODUCT_IDS.PACK_MEGA,
            type: window.CdvPurchase.ProductType.NON_CONSUMABLE,
            platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
          },
        ]);

        // Setup event handlers
        this.store.when()
          .productUpdated((product) => {
            console.log("Product updated:", product.id);
            this.products[product.id] = product;
          })
          .approved((transaction) => {
            console.log("Transaction approved:", transaction.transactionId);
            transaction.verify();
          })
          .verified((receipt) => {
            console.log("Receipt verified - checking product type");
            
            // Check if this is a subscription or consumable
            const isSubscription = receipt.transactions.some(t => 
              t.products.some(p => p.id === PRODUCT_IDS.MONTHLY)
            );
            
            const isSMSPurchase = receipt.transactions.some(t => 
              t.products.some(p => 
                p.id === PRODUCT_IDS.SMS_10 || 
                p.id === PRODUCT_IDS.SMS_50 || 
                p.id === PRODUCT_IDS.SMS_100
              )
            );
            
            if (isSubscription) {
              console.log("Subscription verified - Setting premium status");
              
              // Find transaction ID for syncing
              let transactionId = null;
              for (const t of receipt.transactions) {
                if (t.products.some(p => p.id === PRODUCT_IDS.MONTHLY)) {
                  transactionId = t.transactionId;
                  break;
                }
              }
              
              receipt.finish();
              this.isPremiumActive = true;
              this._savePremiumStatus(true);
              localStorage.setItem("skybday_seen_subscription", "true");
              
              // Dispatch event with transaction ID for backend sync
              window.dispatchEvent(new CustomEvent("skybday_purchase_complete", { 
                detail: { 
                  premium: true,
                  transactionId: transactionId,
                  productId: PRODUCT_IDS.MONTHLY
                } 
              }));
            } else if (isSMSPurchase) {
              console.log("SMS consumable verified - dispatching event");
              // Find which SMS product was purchased
              for (const t of receipt.transactions) {
                for (const p of t.products) {
                  if ([PRODUCT_IDS.SMS_10, PRODUCT_IDS.SMS_50, PRODUCT_IDS.SMS_100].includes(p.id)) {
                    const creditsMap = {
                      [PRODUCT_IDS.SMS_10]: 10,
                      [PRODUCT_IDS.SMS_50]: 50,
                      [PRODUCT_IDS.SMS_100]: 100,
                    };
                    const credits = creditsMap[p.id] || 0;
                    console.log(`SMS purchase verified: ${p.id} = ${credits} credits`);
                    
                    // Dispatch event for SMS purchase
                    window.dispatchEvent(new CustomEvent("skybday_sms_purchase_complete", { 
                      detail: { 
                        productId: p.id,
                        credits: credits,
                        transactionId: t.transactionId
                      } 
                    }));
                  }
                }
              }
              receipt.finish();
            } else {
              // Unknown product type, just finish
              receipt.finish();
            }
          })
          .finished((transaction) => {
            console.log("Transaction finished:", transaction.transactionId);
          })
          .receiptUpdated((receipt) => {
            this._checkPremiumStatus();
            // Also check for pending SMS consumables
            this._checkPendingSMSTransactions(receipt);
          });

        // Initialize the store
        await this.store.initialize([window.CdvPurchase.Platform.APPLE_APPSTORE]);
        
        // Refresh products
        await this.store.update();
        
        // Check existing premium status
        this._checkPremiumStatus();
        
        console.log("StoreKit initialized successfully");
        this.isInitialized = true;
        return true;
      } else if (!this.isNative) {
        // Web fallback - check local storage BUT don't auto-grant premium
        // Premium on web should only be granted for admin users (checked elsewhere)
        const savedStatus = localStorage.getItem("skybday_premium_status");
        // Only restore premium if it was legitimately earned
        // For demo/testing purposes, web users don't get automatic premium
        this.isPremiumActive = false; // Web users must use native app for premium
        this.isInitialized = true;
        console.log("Web mode - premium requires native app");
        return true;
      } else {
        console.warn("CdvPurchase not available");
        this.isInitialized = true;
        return true;
      }
    } catch (error) {
      console.error("Failed to initialize purchases:", error);
      this.isInitialized = true; // Still mark as initialized to prevent blocking
      return false;
    }
  }

  _checkPremiumStatus() {
    if (!this.store) return;
    
    try {
      const product = this.store.get(PRODUCT_IDS.MONTHLY);
      if (product && product.owned) {
        this.isPremiumActive = true;
        this._savePremiumStatus(true);
      } else {
        // Check local receipt
        const localReceipt = this.store.localReceipt;
        if (localReceipt && localReceipt.hasActiveSubscription) {
          this.isPremiumActive = true;
          this._savePremiumStatus(true);
        }
      }
    } catch (error) {
      console.error("Error checking premium status:", error);
    }
  }

  _checkPendingSMSTransactions(receipt) {
    // Check for unfinished SMS consumable transactions
    if (!receipt || !receipt.transactions) return;
    
    const smsProductIds = [PRODUCT_IDS.SMS_10, PRODUCT_IDS.SMS_50, PRODUCT_IDS.SMS_100];
    
    for (const transaction of receipt.transactions) {
      // Check if transaction is pending/unfinished
      if (transaction.state === "approved" || transaction.state === "initiated") {
        for (const product of transaction.products || []) {
          if (smsProductIds.includes(product.id)) {
            console.log(`Found pending SMS transaction: ${product.id}, tx: ${transaction.transactionId}`);
            // Dispatch event to sync with backend
            window.dispatchEvent(new CustomEvent("skybday_sms_pending_sync", {
              detail: {
                productId: product.id,
                transactionId: transaction.transactionId
              }
            }));
          }
        }
      }
    }
  }

  _savePremiumStatus(isPremium) {
    localStorage.setItem("skybday_premium_status", isPremium ? "true" : "false");
  }

  async getOfferings() {
    if (!this.isInitialized) {
      console.warn("PurchaseService not initialized");
      return null;
    }

    try {
      if (this.isNative && this.store) {
        const product = this.store.get(PRODUCT_IDS.MONTHLY);
        
        if (product) {
          return {
            current: {
              identifier: "default",
              availablePackages: [
                {
                  identifier: "$rc_monthly",
                  packageType: "monthly",
                  product: {
                    identifier: product.id,
                    title: product.title || "SkyBday Premium",
                    description: product.description || "Toutes les fonctionnalités illimitées",
                    priceString: product.pricing?.price || "CHF 5.00",
                    price: product.pricing?.priceMicros ? product.pricing.priceMicros / 1000000 : 5.0,
                  },
                },
              ],
            },
          };
        }
      }
      
      // Fallback offerings
      return {
        current: {
          identifier: "default",
          availablePackages: [
            {
              identifier: "$rc_monthly",
              packageType: "monthly",
              product: {
                identifier: PRODUCT_IDS.MONTHLY,
                title: "SkyBday Premium",
                description: "Toutes les fonctionnalités illimitées",
                priceString: "CHF 5.00",
                price: 5.0,
              },
            },
          ],
        },
      };
    } catch (error) {
      console.error("Failed to get offerings:", error);
      return null;
    }
  }

  async getCustomerInfo() {
    return {
      entitlements: {
        active: this.isPremiumActive ? { [ENTITLEMENT_ID]: true } : {},
      },
    };
  }

  async isPremium() {
    return this.isPremiumActive;
  }

  async purchaseMonthly() {
    if (!this.isInitialized) {
      // Try to initialize if not already done
      console.log("PurchaseService not initialized, attempting to initialize...");
      await this.initialize();
    }

    try {
      if (this.isNative && this.store) {
        console.log("=== STARTING STOREKIT PURCHASE FLOW ===");
        console.log("Product ID:", PRODUCT_IDS.MONTHLY);
        
        // Refresh products first to ensure we have latest data
        try {
          console.log("Refreshing store...");
          await this.store.update();
          console.log("Store refreshed successfully");
        } catch (updateError) {
          console.warn("Store update failed, continuing with cached data:", updateError);
        }
        
        // List all available products
        const allProducts = this.store.products;
        console.log("All registered products:", allProducts.map(p => ({ id: p.id, canPurchase: p.canPurchase, owned: p.owned })));
        
        const product = this.store.get(PRODUCT_IDS.MONTHLY);
        console.log("Premium product lookup result:", product ? {
          id: product.id,
          title: product.title,
          canPurchase: product.canPurchase,
          owned: product.owned,
          offers: product.offers?.length || 0
        } : "NOT FOUND");
        
        if (!product) {
          console.error("=== PRODUCT NOT FOUND IN STORE ===");
          console.error("Expected product ID:", PRODUCT_IDS.MONTHLY);
          console.error("Make sure the product is configured in App Store Connect with status 'Ready to Submit'");
          return {
            success: false,
            productNotFound: true,
            message: "Abonnement temporairement indisponible. Veuillez réessayer."
          };
        }

        // Check if product can be purchased
        if (!product.canPurchase) {
          console.log("Product cannot be purchased. Owned:", product.owned, "State:", product.state);
          if (product.owned) {
            // User already owns this product
            this.isPremiumActive = true;
            this._savePremiumStatus(true);
            return {
              success: true,
              alreadyOwned: true,
              message: "Vous êtes déjà Premium !"
            };
          }
          return {
            success: false,
            cannotPurchase: true,
            message: "Ce produit n'est pas disponible à l'achat. Vérifiez qu'il est bien configuré dans App Store Connect."
          };
        }

        // Get the offer (for subscriptions)
        const offer = product.getOffer();
        console.log("Offer for subscription:", offer ? {
          id: offer.id,
          pricingPhases: offer.pricingPhases?.length || 0
        } : "NO OFFER AVAILABLE");
        
        if (!offer) {
          console.error("=== NO OFFER AVAILABLE ===");
          console.error("The subscription product exists but has no offer configured.");
          console.error("Check App Store Connect: Subscription > Pricing > Add Pricing");
          return {
            success: false,
            noOffer: true,
            message: "L'offre d'abonnement n'est pas configurée. Vérifiez les prix dans App Store Connect."
          };
        }

        // Initiate purchase - THIS SHOULD SHOW THE STOREKIT DIALOG
        console.log("=== INITIATING STOREKIT ORDER ===");
        console.log("Ordering offer:", offer.id);
        const result = await this.store.order(offer);
        console.log("Order result:", result);
        
        if (result && result.isError) {
          console.error("Purchase error:", result.code, result.message);
          if (result.code === window.CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
            return { success: false, cancelled: true };
          }
          return {
            success: false,
            error: true,
            message: result.message || "Erreur lors de l'achat"
          };
        }

        // Purchase initiated successfully
        console.log("=== PURCHASE INITIATED SUCCESSFULLY ===");
        return {
          success: true,
          pending: true, // Will be completed via callbacks
        };
      } else if (this.isNative && !this.store) {
        // Native but store not available (StoreKit not loaded)
        console.error("Native app but CdvPurchase store not available");
        return {
          success: false,
          storeUnavailable: true,
          message: "Le service d'achat n'est pas disponible. Veuillez fermer et rouvrir l'application."
        };
      } else {
        // Web fallback - cannot process real purchases
        return {
          success: false,
          message: "Les achats ne sont disponibles que sur l'application iOS",
          webOnly: true,
        };
      }
    } catch (error) {
      console.error("Purchase failed with exception:", error);

      if (error.message?.includes("cancel") || error.code === "PAYMENT_CANCELLED") {
        return { success: false, cancelled: true };
      }

      // Return error info instead of throwing
      return {
        success: false,
        error: true,
        message: error.message || "Erreur inattendue lors de l'achat"
      };
    }
  }

  /**
   * Purchase SMS credits (consumable)
   * @param {string} productId - One of SMS_10, SMS_50, SMS_100
   * @param {function} onSuccess - Callback when purchase is successful, receives credits amount
   */
  async purchaseSMSCredits(productId, onSuccess) {
    if (!this.isInitialized) {
      // Try to initialize if not already done
      console.log("PurchaseService not initialized for SMS, attempting to initialize...");
      await this.initialize();
      
      if (!this.isInitialized) {
        return {
          success: false,
          notInitialized: true,
          message: "Le service d'achat n'est pas disponible. Veuillez fermer et rouvrir l'application."
        };
      }
    }

    // Map product ID to credits
    const creditsMap = {
      [PRODUCT_IDS.SMS_10]: 10,
      [PRODUCT_IDS.SMS_50]: 50,
      [PRODUCT_IDS.SMS_100]: 100,
    };

    try {
      if (this.isNative && this.store) {
        const product = this.store.get(productId);
        
        if (!product) {
          console.error("SMS Product not found:", productId);
          throw new Error("Produit non disponible");
        }

        // Setup one-time event listener for SMS purchase completion
        const handleSMSPurchase = (event) => {
          const { productId: purchasedProductId, credits, transactionId } = event.detail;
          if (purchasedProductId === productId) {
            console.log(`SMS purchase completed: ${credits} credits, transaction: ${transactionId}`);
            window.removeEventListener("skybday_sms_purchase_complete", handleSMSPurchase);
            if (onSuccess) {
              onSuccess(credits, transactionId);
            }
          }
        };
        window.addEventListener("skybday_sms_purchase_complete", handleSMSPurchase);

        // Get the offer and order
        const offer = product.getOffer();
        if (!offer) {
          window.removeEventListener("skybday_sms_purchase_complete", handleSMSPurchase);
          throw new Error("Offre non disponible");
        }

        const result = await this.store.order(offer);
        
        if (result && result.isError) {
          window.removeEventListener("skybday_sms_purchase_complete", handleSMSPurchase);
          if (result.code === window.CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
            return { success: false, cancelled: true };
          }
          throw new Error(result.message || "Erreur lors de l'achat");
        }

        return {
          success: true,
          pending: true,
          credits: creditsMap[productId] || 0,
        };
      } else {
        // Web fallback
        return {
          success: false,
          message: "Les achats ne sont disponibles que sur l'application iOS",
          webOnly: true,
        };
      }
    } catch (error) {
      console.error("SMS Purchase failed:", error);

      if (error.message?.includes("cancel") || error.code === "PAYMENT_CANCELLED") {
        return { success: false, cancelled: true };
      }

      throw error;
    }
  }

  /**
   * Purchase Message Pack (non-consumable)
   * @param {string} productId - One of PACK_PROCHES, PACK_BUSINESS, etc.
   * @param {function} onSuccess - Callback when purchase is successful
   */
  async purchaseMessagePack(productId, onSuccess) {
    if (!this.isInitialized) {
      console.log("PurchaseService not initialized for message pack, attempting to initialize...");
      await this.initialize();
      
      if (!this.isInitialized) {
        return {
          success: false,
          notInitialized: true,
          message: "Le service d'achat n'est pas disponible. Veuillez fermer et rouvrir l'application."
        };
      }
    }

    try {
      if (this.isNative && this.store) {
        const product = this.store.get(productId);
        
        if (!product) {
          console.error("Message Pack product not found:", productId);
          throw new Error("Produit non disponible");
        }

        console.log("Purchasing message pack:", productId, product);

        // Set up one-time handler for this specific purchase
        const purchasePromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Timeout de l'achat"));
          }, 120000); // 2 minute timeout

          const handler = this.store.when()
            .verified((receipt) => {
              const isMessagePackPurchase = receipt.transactions.some(t => 
                t.products.some(p => p.id === productId)
              );

              if (isMessagePackPurchase) {
                clearTimeout(timeout);
                console.log("Message pack purchase verified:", productId);
                
                // Get transaction ID
                const transaction = receipt.transactions.find(t => 
                  t.products.some(p => p.id === productId)
                );
                const transactionId = transaction?.transactionId || `pack_${Date.now()}`;
                
                // Finish the transaction
                receipt.finish();
                
                // Call success callback with transaction ID
                if (onSuccess) {
                  onSuccess(transactionId);
                }
                
                resolve({ success: true, transactionId });
              }
            });
        });

        // Initiate purchase
        const offer = product.getOffer();
        if (offer) {
          await offer.order();
        } else {
          throw new Error("Aucune offre disponible pour ce produit");
        }

        return await purchasePromise;
      } else {
        // Web fallback
        return {
          success: false,
          message: "Les achats ne sont disponibles que sur l'application iOS",
          webOnly: true,
        };
      }
    } catch (error) {
      console.error("Message Pack Purchase failed:", error);

      if (error.message?.includes("cancel") || error.code === "PAYMENT_CANCELLED") {
        return { success: false, cancelled: true };
      }

      throw error;
    }
  }

  async restorePurchases() {
    if (!this.isInitialized) {
      throw new Error("PurchaseService not initialized");
    }

    try {
      if (this.isNative && this.store) {
        await this.store.restorePurchases();
        
        // Wait a bit for the store to update
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        this._checkPremiumStatus();
        
        return await this.getCustomerInfo();
      } else {
        return await this.getCustomerInfo();
      }
    } catch (error) {
      console.error("Restore failed:", error);
      throw error;
    }
  }

  async login(userId) {
    // StoreKit doesn't require explicit login
    // Premium status is tied to Apple ID
    return await this.getCustomerInfo();
  }

  async logout() {
    // Nothing to do for StoreKit
  }
}

// Singleton instance
export const purchaseService = new PurchaseService();
export default purchaseService;
